import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and CSS display grid for height auto animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on CSS display grid and inert attributes to create smooth height animations
 * when expanding/collapsing content areas.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }
  /**
   * Returns the plus SVG as a string
   */
  plusSVG() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none" class="icon-plus" style="display: block;">
        <path d="M15 27.5C21.9036 27.5 27.5 21.9036 27.5 15C27.5 8.09644 21.9036 2.5 15 2.5C8.09644 2.5 2.5 8.09644 2.5 15C2.5 21.9036 8.09644 27.5 15 27.5Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
        <path d="M10 15H20" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
        <path d="M15 10V20" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
    `;
  }

  /**
   * Returns the minus SVG as a string
   */
  minusSVG() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none" class="icon-minus" style="display: block;">
        <path d="M15 27.5C21.9036 27.5 27.5 21.9036 27.5 15C27.5 8.09644 21.9036 2.5 15 2.5C8.09644 2.5 2.5 8.09644 2.5 15C2.5 21.9036 8.09644 27.5 15 27.5Z" stroke="#00AB66" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
        <path d="M10 15H20" stroke="#00AB66" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
    `;
  }
  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with CSS grid animations to provide smooth height transitions.
   */
  toggleDisclosure = () => {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
  
    // Toggle aria-expanded
    trigger.setAttribute('aria-expanded', String(!expanded));

    // Update aria-label
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    );

    // Toggle inert state
    content.inert = expanded;

    // --- Toggle icon ---
    const iconWrapper = trigger.querySelector('.svg-wrapper');
    if (iconWrapper) {
      iconWrapper.innerHTML = expanded ? this.plusSVG() : this.minusSVG();
    }
  };
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}
